/**
 * Feature to Permission Mapping
 * 
 * Maps feature-level keys (used in code) to action-level permissions (stored in role_permissions table).
 * 
 * This mapping layer resolves the mismatch between:
 * - Feature keys: asset_management, repairs, etc. (used in hasAccess(), FeatureGate)
 * - Permission keys: view_assets, view_repairs, etc. (stored in role_permissions table)
 * 
 * Access to a feature is granted ONLY if ALL required permissions are true.
 * If a feature is not mapped, access is denied (fail closed).
 */

/**
 * Feature to Permission Mapping
 * 
 * Each feature maps to an array of required permission keys.
 * User must have ALL listed permissions (has_access = true) to access the feature.
 * 
 * @type {Object.<string, string[]>}
 */
export const FEATURE_PERMISSION_MAP = {
  // Asset Management Feature
  'asset_management': ['view_assets'],
  
  // Repairs Feature
  'repairs': ['view_repairs'], // Users need at least view_repairs (view_own_repairs or view_all_repairs grants view_repairs)
  
  // Inspections Feature (part of repairs workflow)
  'inspections': ['view_repairs'],
  
  // File Management Feature
  'file_management': ['view_files'],
  
  // User Management Feature
  'user_management': ['view_users'],
  
  // Organization Management Feature
  'organization_management': ['manage_own_organizations'], // At least manage own orgs
  
  // Admin Panel Feature
  'admin_panel': ['view_admin'],
  
  // Live Map Feature
  'live_map': ['view_map'],
  
  // Reports Feature
  'reports': ['admin_reporting'],
  
  // Vehicle Tracking Feature
  // Note: No direct permission exists, but vehicle tracking is typically part of asset management
  // Using view_assets as a reasonable default - may need to add vehicle_tracking permission later
  'vehicle_tracking': ['view_assets'], // Temporary mapping - consider adding vehicle_tracking permission
  
  // UWB Tracking Feature
  // Note: No direct permission exists, using view_map as UWB is map-related
  'uwb_tracking': ['view_map'], // Temporary mapping - consider adding uwb_tracking permission
  
  // BLE Tracking Feature (Legacy)
  // Note: No direct permission exists, using view_map as BLE is map-related
  'ble_tracking': ['view_map'], // Temporary mapping - consider adding ble_tracking permission
  
  // Vision Tracking Feature
  // Note: No direct permission exists, using view_assets as vision tracks assets
  'vision_tracking': ['view_assets'], // Temporary mapping - consider adding vision_tracking permission
  
  // Blueprint Editor Feature
  // Note: No direct permission exists, using view_map as blueprints are map-related
  'blueprint_editor': ['view_map'], // Temporary mapping - consider adding blueprint_editor permission
  
  // Vendor Management Feature
  // Note: No direct permission exists, using view_repairs as vendors are repair-related
  'vendor_management': ['view_repairs'], // Temporary mapping - consider adding vendor_management permission
  
  // Workflows Feature
  // Note: Workflows are repair-related, using view_repairs
  'workflows': ['view_repairs'],
  
  // API Access Feature
  // Note: No direct permission exists, using view_admin as API access is typically admin-only
  'api_access': ['view_admin'], // Temporary mapping - consider adding api_access permission
};

/**
 * Get required permissions for a feature
 * @param {string} featureKey - Feature identifier
 * @returns {string[]|null} Array of required permission keys, or null if feature is not mapped
 */
export const getRequiredPermissions = (featureKey) => {
  if (!featureKey || typeof featureKey !== 'string') {
    return null;
  }
  
  return FEATURE_PERMISSION_MAP[featureKey] || null;
};

/**
 * Check if a feature is mapped
 * @param {string} featureKey - Feature identifier
 * @returns {boolean} True if feature has a permission mapping
 */
export const isFeatureMapped = (featureKey) => {
  return featureKey in FEATURE_PERMISSION_MAP;
};

/**
 * Get all mapped feature keys
 * @returns {string[]} Array of all feature keys that have mappings
 */
export const getMappedFeatures = () => {
  return Object.keys(FEATURE_PERMISSION_MAP);
};
